import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';
import { createServerAPI, updateServerAPI, getServerDetailsAPI } from '../services/serverService';

const Spinner = () => <div className="spinner"></div>;

const AddServerModal = ({ closeModal, refreshList, serverToEdit }) => {
  const navigate = useNavigate();
  const isEditMode = Boolean(serverToEdit);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  
  const [formData, setFormData] = useState({
    companyName: '', website: '', companyEmail: '', phoneNumber: '', openingHours: '',
    serverName: '', cqcProviderID: '', cqcLocationID: '', companyAddress: '',
    contractStartDate: '', contractEndDate: '', isLite: true, maxUsers: ''
  });

  useEffect(() => {
    if (isEditMode && serverToEdit) {
      const fetchDetails = async () => {
        setIsLoadingDetails(true);
        try {
          const response = await getServerDetailsAPI(serverToEdit.server_id);
          const serverData = response.data.data;
          setFormData({
            companyName: serverData.companyName || '',
            website: serverData.website || '',
            companyEmail: serverData.companyEmail || '',
            phoneNumber: serverData.phoneNumber || '',
            openingHours: serverData.openingHours || '',
            serverName: serverToEdit.server_id,
            cqcProviderID: serverData.cqcProviderID || '',
            cqcLocationID: serverData.cqcLocationID || '',
            companyAddress: serverData.companyAddress || '',
            contractStartDate: serverData.contractStartDate || '',
            contractEndDate: serverData.contractEndDate || '',
            isLite: serverData.isLite ?? true,
            maxUsers: serverData.maxUsers || ''
          });
        } catch (error) {
          console.error("Failed to fetch server details", error);
          setApiError("Could not load server details.");
        } finally {
          setIsLoadingDetails(false);
        }
      };
      fetchDetails();
    }
  }, [isEditMode, serverToEdit]);

  const [isSaving, setIsSaving] = useState(false);
  const [apiError, setApiError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue = type === 'checkbox' ? checked : value;
    
    if (name === 'isLite') {
        finalValue = value === 'true';
    }

    setFormData(prevState => ({ ...prevState, [name]: finalValue }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.companyName.trim()) errors.companyName = 'Company Name is required.';
    if (!isEditMode && !formData.serverName.trim()) errors.serverName = 'Company Server name is required.';
    if (!formData.companyEmail.trim()) {
      errors.companyEmail = 'Company Email is required.';
    } else if (!/\S+@\S+\.\S+/.test(formData.companyEmail)) {
      errors.companyEmail = 'Email address is invalid.';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e, shouldRedirect = false) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSaving(true);
    setApiError('');
    
    const apiPayload = { 
        companyName: formData.companyName, website: formData.website, companyEmail: formData.companyEmail,
        companyPhoneNumber: formData.phoneNumber, openingHours: formData.openingHours, 
        cqcProviderID: formData.cqcProviderID, cqcLocationID: formData.cqcLocationID, companyAddress: formData.companyAddress,
        contractStartDate: formData.contractStartDate, contractEndDate: formData.contractEndDate,
        isLite: Boolean(formData.isLite),
        maxUsers: Number(formData.maxUsers) || 0
    };

    try {
      let targetServerId = isEditMode ? serverToEdit.server_id : formData.serverName;

      if (isEditMode) {
        await updateServerAPI(targetServerId, apiPayload);
        alert('Server updated successfully!');
      } else {
        apiPayload.serverName = formData.serverName;
        await createServerAPI(apiPayload);
        alert('Server created successfully!');
      }
      
      refreshList();

      if (shouldRedirect && targetServerId) {
          navigate(`/servers/${targetServerId}`, { state: { openEditModal: true } });
      } else {
          closeModal();
      }

    } catch (err) {
      console.error("Submit Error:", err);
      const errorMessage = err.response?.data?.message || 'Operation failed. Please check the details and try again.';
      setApiError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close-button" onClick={closeModal}>×</button>
        <h2>{isEditMode ? 'Edit Server' : 'Add New Server'}</h2>
        {isLoadingDetails ? (
          <p>Loading details...</p>
        ) : (
          <form>
            <div className="form-grid">
              <div className="form-group"><label>Company Name</label><input name="companyName" value={formData.companyName} onChange={handleChange} placeholder="e.g., Health Automated Ltd" required />{validationErrors.companyName && <p className="validation-error">{validationErrors.companyName}</p>}</div>
              <div className="form-group"><label>Website</label><input name="website" value={formData.website} onChange={handleChange} placeholder="e.g., https://example.com" /></div>
              <div className="form-group"><label>Company Email</label><input type="email" name="companyEmail" value={formData.companyEmail} onChange={handleChange} placeholder="e.g., contact@example.com" required />{validationErrors.companyEmail && <p className="validation-error">{validationErrors.companyEmail}</p>}</div>
              <div className="form-group"><label>Company Phone Number</label><input name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} placeholder="e.g., +44123456789" /></div>
              <div className="form-group"><label>Opening Hours</label><input name="openingHours" value={formData.openingHours} onChange={handleChange} placeholder="e.g., 9:00 AM - 6:00 PM" /></div>
              <div className="form-group"><label>Server ID</label><input name="serverName" value={formData.serverName} onChange={handleChange} placeholder="e.g., architecture.care" required disabled={isEditMode} />{validationErrors.serverName && <p className="validation-error">{validationErrors.serverName}</p>}</div>
              <div className="form-group">
                <label>Billing Tier</label>
                <select name="isLite" value={formData.isLite.toString()} onChange={handleChange}>
                  <option value="true">Freemium (Lite)</option>
                  <option value="false">Premium</option>
                </select>
              </div>
              <div className="form-group">
                <label>Max Total Staff</label>
                <input type="number" name="maxUsers" value={formData.maxUsers} onChange={handleChange} placeholder="e.g., 25" />
              </div>

              <div className="form-group"><label>CQC Provider ID</label><input name="cqcProviderID" value={formData.cqcProviderID} onChange={handleChange} placeholder="HA000000001" /></div>
              <div className="form-group"><label>Contract Start Date</label><input type="date" name="contractStartDate" value={formData.contractStartDate} onChange={handleChange} /></div>
              <div className="form-group"><label>Contract End Date</label><input type="date" name="contractEndDate" value={formData.contractEndDate} onChange={handleChange} /></div>
            </div>
            
            <div className="form-group full-width"><label>CQC Location ID</label><input name="cqcLocationID" value={formData.cqcLocationID} onChange={handleChange} placeholder="HALOC0000000001" style={{ width: '48%'}} /></div>
            <div className="form-group full-width"><label>Company Full Address</label><textarea name="companyAddress" value={formData.companyAddress} onChange={handleChange} placeholder="Enter the full address"></textarea></div>
            
            {apiError && <p className="error-message">{apiError}</p>}
            
            <div className="modal-actions" style={{ gap: '10px' }}>
                <button type="button" className="button-secondary" onClick={closeModal} disabled={isSaving}>
                    Cancel
                </button>
                
                <button 
                    type="button" 
                    className={isEditMode ? "button-primary" : "button-primary-outline"} 
                    onClick={(e) => handleSubmit(e, false)} 
                    disabled={isSaving}
                >
                    {isSaving ? <Spinner /> : (isEditMode ? 'Save Changes' : 'Quick Add')}
                </button>

                {!isEditMode && (
                    <button 
                        type="button" 
                        className="button-primary" 
                        onClick={(e) => handleSubmit(e, true)} 
                        disabled={isSaving}
                        style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                    >
                        {isSaving ? <Spinner /> : <>Configure Server <FaArrowRight /></>}
                    </button>
                )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
export default AddServerModal;